/**
 * Get the stack trace for an error that is passed in to the handler
 * @returns
 */
export function getStackTrace(error: any): string {
    // this works for kendo grid errors and angular errors, but it is possible there could be more formats
    // Expand on this method as needed.
    return error?.stack ?? '';
}

/**
 * Returns true if any stack frame's function token clearly refers to the registered component.
 *
 */
export function stackTraceIncludesComponent(stackTrace: string, component: string): boolean {
    if (!stackTrace || !component) {
        return false;
    }

    const name = component;
    //make the component name regex safe
    const escapedComponentName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Only check the first 40 lines (after the header) to avoid stacks that are super deep
    const lines = stackTrace.split('\n').slice(0, 41);

    // Try to extract the "function token" part of each frame when possible.
    // Examples:
    //   "    at ComponentName.ngOnInit (main.js:1:2345)"  => "ComponentName.ngOnInit"
    //   "ComponentName@main.js:1:2345"                    => "ComponentName"
    const fnTokenExtractors = [
        // Chrome/V8: "at <func>(<loc>)"
        /^\s*at\s+(.*?)\s+\(/,
        // Chrome/V8 w/o location: "at <func>"
        /^\s*at\s+(.*)\s*$/,
        // Safari/Firefox: "<func>@<loc>"
        /^(.*?)@/
    ];

    const functionTokens = lines.map((l) => {
        for (const rx of fnTokenExtractors) {
            const m = l.match(rx);
            if (m && m[1]) {
                return m[1].trim();
            }
        }
        return ''; // unknown format; we'll still let the framePatterns scan the whole line
    });

    // First, try to match against extracted function tokens (best signal).
    for (const tok of functionTokens) {
        if (!tok) {
            continue;
        }

        // ignore tokens that look like URLs/paths (prevents "/t.component.ts" false positives)
        if (/[\\/]/.test(tok)) {
            continue;
        }

        // exact component name match, or component prefix followed by a separator ('.', '@', ' ', '(')
        if (tok === name) {
            return true;
        }
        if (/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(tok)) {
            // single identifier (e.g., "ComponentName")
            if (tok === name) {
                return true;
            }
        } else {
            // composite token (e.g., "ComponentName.ngOnInit")
            if (new RegExp(`(^|\\b)${escapedComponentName}(?=\\.|\\s|@|\\(|$)`).test(tok)) {
                return true;
            }
        }
    }

    // Common function token shapes we want to match:
    //   Chrome/V8: "at ComponentName.method (file:line:col)"
    //              "at new ComponentName (file:line:col)"
    //   Safari/Firefox: "ComponentName@file:line:col"
    //   Generic TS names: "ComponentName.ngOnInit", "ComponentName.<anonymous>"
    //
    // Anchor to word boundaries around the component *name* and then allow
    // typical separators (dot, whitespace, '@', '(') that indicate a function context.
    const framePatterns = [
        // V8/Chrome: "at ComponentName..." or "at new ComponentName..."
        new RegExp(`\\bat\\s+(?:new\\s+)?${escapedComponentName}(?:(?:\\.|\\s|@|\\(|$))`),

        // Method token anywhere in the frame: "ComponentName.someMethod"
        // Guard against filenames/paths like "/t.component.ts" and script names like "m.js".
        new RegExp(
            `(?<![/])\\b${escapedComponentName}\\.(?!` +
                `(?:js|jsx|ts|tsx|mjs|cjs|map|css|html)\\b` + // not a bare extension right after the dot
                `)[A-Za-z_$][A-Za-z0-9_$]*\\b(?!\\.(?:js|jsx|ts|tsx|mjs|cjs|map|css|html)\\b)` // and not followed by .ext
        ),

        // Safari/Firefox token: "ComponentName@"
        new RegExp(`\\b${escapedComponentName}@`),

        // Minimal fallback: " at ComponentName " (space or EOL after the exact name)
        new RegExp(`\\bat\\s+${escapedComponentName}(?:\\s|$|\\()`)
    ];
    // As a fallback, test the raw frame lines with stricter patterns.
    return lines.some((l) => framePatterns.some((p) => p.test(l)));
}
